'use client';

import { useEffect, useMemo } from 'react';

interface WordTiming {
  w: string;
  start: number;
  end: number;
}

interface WordHighlightedTextProps {
  text: string;
  wordTimings?: WordTiming[];
  // New API (preferred)
  activeWordIndex?: number;  // Now passed from parent (optimized to only change when word changes)
  // Old API (for backward compatibility)
  currentTime?: number;
  isPlaying?: boolean;
  className?: string;
  enableWindowing?: boolean; // Enable smart text windowing to prevent scrolling
  windowBefore?: number; // Minimum words to show before active word (default: 7)
  windowAfter?: number; // Minimum words to show after active word (default: 5)
}

export default function WordHighlightedText({ 
  text, 
  wordTimings, 
  activeWordIndex: providedActiveWordIndex,
  currentTime,
  isPlaying,
  className = '',
  enableWindowing = false,
  windowBefore = 7,
  windowAfter = 5
}: WordHighlightedTextProps) {
  // Calculate active word index from currentTime if using old API
  const calculatedActiveWordIndex = useMemo(() => {
    if (providedActiveWordIndex !== undefined) {
      return providedActiveWordIndex;
    }
    
    if (currentTime === undefined || !wordTimings || wordTimings.length === 0) {
      return -1;
    }

    // Find the word that should be highlighted based on current time
    for (let i = 0; i < wordTimings.length; i++) {
      if (currentTime >= wordTimings[i].start && currentTime <= wordTimings[i].end) {
        return i;
      }
    }
    
    // If we're past all words, return last word index
    if (currentTime > wordTimings[wordTimings.length - 1].end) {
      return wordTimings.length - 1;
    }
    
    return -1;
  }, [providedActiveWordIndex, currentTime, wordTimings]);

  const activeWordIndex = calculatedActiveWordIndex;
  
  // ✅ PERFORMANCE: Removed console.logs for better performance
  
  // Calculate visible word window - SENTENCE-BASED HIDING
  const visibleWords = useMemo(() => {
    if (!wordTimings || wordTimings.length === 0) {
      return [];
    }

    // If windowing is disabled, show all words
    if (!enableWindowing) {
      return wordTimings.map((timing, idx) => ({ timing, originalIdx: idx }));
    }

    // If no active word yet (start of story), show ALL words
    if (activeWordIndex < 0) {
      return wordTimings.map((timing, idx) => ({ timing, originalIdx: idx }));
    }

    // SENTENCE-BASED WINDOWING STRATEGY:
    // 1. Detect sentence boundaries (. ! ?)
    // 2. Find which sentence contains the active word
    // 3. Hide all COMPLETED sentences (before current sentence)
    // 4. Show current sentence + all future sentences
    // Result: Text naturally scrolls up as sentences are completed
    
    const totalWords = wordTimings.length;
    
    // Find sentence boundaries by looking for sentence-ending punctuation
    const sentenceEnds: number[] = []; // Indices where sentences end
    wordTimings.forEach((timing, idx) => {
      const word = timing.w.trim();
      // Check if word ends with sentence-ending punctuation
      if (word.match(/[.!?]["']?$/)) {
        sentenceEnds.push(idx);
      }
    });
    
    // If no sentence boundaries found, use paragraph-based logic (every ~15-20 words)
    if (sentenceEnds.length === 0) {
      // Fallback: Hide words in chunks
      const wordsPerChunk = 20;
      const currentChunk = Math.floor(activeWordIndex / wordsPerChunk);
      const startIdx = Math.max(0, currentChunk * wordsPerChunk);
      return wordTimings.slice(startIdx).map((timing, idx) => ({ 
        timing, 
        originalIdx: startIdx + idx 
      }));
    }
    
    // Find the current sentence (the sentence containing active word)
    let currentSentenceStart = 0;
    let currentSentenceEnd = sentenceEnds[0];
    
    for (let i = 0; i < sentenceEnds.length; i++) {
      const sentenceEnd = sentenceEnds[i];
      const sentenceStart = i === 0 ? 0 : sentenceEnds[i - 1] + 1;
      
      if (activeWordIndex >= sentenceStart && activeWordIndex <= sentenceEnd) {
        currentSentenceStart = sentenceStart;
        currentSentenceEnd = sentenceEnd;
        break;
      } else if (activeWordIndex > sentenceEnd && (i === sentenceEnds.length - 1 || activeWordIndex < sentenceEnds[i + 1])) {
        // Active word is between sentences or in last sentence
        currentSentenceStart = sentenceEnd + 1;
        currentSentenceEnd = i < sentenceEnds.length - 1 ? sentenceEnds[i + 1] : totalWords - 1;
        break;
      }
    }
    
    // HIDE STRATEGY: Keep some context before current sentence
    // Show previous sentence + current sentence + all future sentences
    const keepPreviousSentences = 1; // Keep 1 previous sentence for context
    
    let startIdx = currentSentenceStart;
    if (keepPreviousSentences > 0) {
      // Find the start of the previous sentence
      const currentSentenceIndex = sentenceEnds.findIndex(end => end >= currentSentenceEnd);
      if (currentSentenceIndex > 0) {
        // Go back to start of previous sentence
        const prevSentenceIndex = Math.max(0, currentSentenceIndex - keepPreviousSentences);
        startIdx = prevSentenceIndex === 0 ? 0 : sentenceEnds[prevSentenceIndex - 1] + 1;
      } else {
        startIdx = 0;
      }
    }
    
    // Show from startIdx to end of text (all future content)
    return wordTimings.slice(startIdx).map((timing, idx) => ({ 
      timing, 
      originalIdx: startIdx + idx 
    }));
  }, [wordTimings, activeWordIndex, enableWindowing, windowBefore, windowAfter]);

  // If no word timings available, render plain text
  if (!wordTimings || wordTimings.length === 0) {
    console.log('[WordHighlightedText] No word timings available, rendering plain text');
    return <p className={className} style={{ whiteSpace: 'pre-wrap' }}>{text}</p>;
  }

  return (
    <p className={className} style={{ whiteSpace: 'pre-wrap' }}>
        {visibleWords.map(({ timing, originalIdx }, displayIdx) => {
          const isActive = originalIdx === activeWordIndex;
          const isRecent = originalIdx === activeWordIndex - 1; // Previous word
          
          return (
            <span key={`word-${originalIdx}`}>
                <span
                  className={`inline-block transition-all duration-300 ease-out ${
                    isActive 
                      ? 'font-bold rounded-lg' 
                      : isRecent
                      ? 'font-semibold'
                      : ''
                  }`}
                  style={{
                    transformOrigin: 'center',
                    // 2026 enhanced design: smooth scale with subtle bounce
                    transform: isActive ? 'scale(1.08)' : 'scale(1)',
                    // Gradient background for modern look
                    background: isActive 
                      ? 'linear-gradient(135deg, #FEF08A 0%, #FDE047 100%)' 
                      : 'transparent',
                    color: isActive ? '#1F2937' : 'inherit',
                    padding: '6px 10px',
                    margin: '0 3px',
                    borderRadius: '8px',
                    position: 'relative',
                    zIndex: isActive ? 10 : 1,
                    // Soft glow effect for emphasis
                    boxShadow: isActive 
                      ? '0 4px 12px rgba(254, 240, 138, 0.5), 0 0 16px rgba(254, 240, 138, 0.25)' 
                      : 'none',
                    // Subtle border for definition
                    border: isActive ? '1px solid rgba(253, 224, 71, 0.6)' : 'none',
                  }}
                >
                  {timing.w}
                </span>
              {displayIdx < visibleWords.length - 1 ? ' ' : ''}
            </span>
          );
        })}
      </p>
  );
}

